import { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/axiosInstance';
import { ApplicationUser } from '../models/ApplicationUser';

const AuthContext = createContext<{
  user: ApplicationUser | null,
  login: (username: string, recaptchaToken: string) => Promise<void>,
  logout: () => Promise<void>,
  loading: boolean
}>({ user: null, login: () => Promise.resolve(), logout: () => Promise.resolve(), loading: false });

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<ApplicationUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get('/logged-in-user');
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (username: string, recaptchaToken: string): Promise<void> => {
    setLoading(true);

    try {
      const { data: loginResult } = await api.post(
        '/login',
        {
          email: username,
          recaptchaToken: recaptchaToken
        });

      if (loginResult.loginSuccessful &&
        loginResult.passkeyOptions) {

        const options = PublicKeyCredential.parseRequestOptionsFromJSON(
          JSON.parse(loginResult.passkeyOptions));

        const credential = await navigator.credentials.get({ publicKey: options });

        const { data: passkeyLoginResult } = await api.post(
          '/passkey-login',
          {
            email: username,
            credential: JSON.stringify(credential)
          });

        setLoading(false);

        if (passkeyLoginResult.passkeyLoginSuccessful &&
          passkeyLoginResult.user) {
          setUser(passkeyLoginResult.user);
          setLoading(false);
        } else {
          throw new Error(passkeyLoginResult.passkeyLoginUnsuccessfulReason);
        }
      } else {
        setLoading(false);
        throw new Error(loginResult.loginUnsuccessfulReason);
      }
    }
    catch (error: any) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    setUser(null);
    await api.get('/logout');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);